/**
 * "Report a problem" dialog (spec §7.4, Task 13): category → description →
 * submit → reference. A duplicate open report for the same interaction and
 * category shows its existing reference with a "Report another issue" button
 * that resubmits with `override: true`. A delivery failure still treats the
 * report as saved — the reference and badge stand regardless of email
 * delivery.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Textarea,
  makeStyles,
  tokens,
  type RadioGroupOnChangeData,
} from '@fluentui/react-components';
import { api } from '../../services/api';
import type { ReportCategory, SupportReportSummary } from '../../types';
import { strings } from '../../strings/en';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
});

const CATEGORY_ORDER: ReportCategory[] = [
  'audio_quality',
  'routing_transfer',
  'caller_id',
  'customer_mapping',
  'other',
];

export interface ReportProblemDialogProps {
  open: boolean;
  interactionId: string;
  onClose: () => void;
  onSubmitted: (report: SupportReportSummary) => void;
}

type Phase = 'form' | 'submitting' | 'result';

export function ReportProblemDialog({ open, interactionId, onClose, onSubmitted }: ReportProblemDialogProps) {
  const styles = useStyles();
  const [category, setCategory] = useState<ReportCategory>(CATEGORY_ORDER[0]);
  const [description, setDescription] = useState('');
  const [phase, setPhase] = useState<Phase>('form');
  const [result, setResult] = useState<SupportReportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setCategory(CATEGORY_ORDER[0]);
      setDescription('');
      setPhase('form');
      setResult(null);
      setError(null);
    }
  }, [open]);

  async function submit(override: boolean) {
    setPhase('submitting');
    setError(null);
    try {
      const report = await api.createSupportReport({
        interaction_id: interactionId,
        category,
        description,
        override,
      });
      setResult(report);
      setPhase('result');
      onSubmitted(report);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send report.');
      setPhase('form');
    }
  }

  const submitting = phase === 'submitting';

  return (
    <Dialog
      open={open}
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{strings.report.dialogTitle}</DialogTitle>
          <DialogContent className={styles.content}>
            {error ? (
              <MessageBar intent="error">
                <MessageBarBody>{error}</MessageBarBody>
              </MessageBar>
            ) : null}

            {phase === 'result' && result ? (
              result.is_duplicate ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    <strong>{strings.report.duplicateTitle}</strong>
                    <div>{strings.report.duplicateBody(result.reference)}</div>
                  </MessageBarBody>
                </MessageBar>
              ) : (
                <MessageBar intent={result.delivery?.succeeded === false ? 'warning' : 'success'}>
                  <MessageBarBody>
                    {result.delivery?.succeeded === false
                      ? strings.report.deliveryFailed(result.reference)
                      : strings.report.submitted(result.reference)}
                  </MessageBarBody>
                </MessageBar>
              )
            ) : (
              <>
                <div className={styles.field}>
                  <label id="report-category-label">{strings.report.category}</label>
                  <RadioGroup
                    aria-labelledby="report-category-label"
                    value={category}
                    onChange={(_event, data: RadioGroupOnChangeData) => setCategory(data.value as ReportCategory)}
                    disabled={submitting}
                  >
                    {CATEGORY_ORDER.map((value) => (
                      <Radio key={value} value={value} label={strings.report.categories[value]} />
                    ))}
                  </RadioGroup>
                </div>
                <div className={styles.field}>
                  <label htmlFor="report-description">{strings.report.description}</label>
                  <Textarea
                    id="report-description"
                    value={description}
                    onChange={(_event, data) => setDescription(data.value)}
                    placeholder={strings.report.descriptionPlaceholder}
                    disabled={submitting}
                    resize="vertical"
                  />
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {phase === 'result' && result ? (
              result.is_duplicate ? (
                <>
                  <Button appearance="secondary" onClick={onClose}>
                    {strings.report.close}
                  </Button>
                  <Button appearance="primary" onClick={() => submit(true)} disabled={submitting}>
                    {strings.report.reportAnother}
                  </Button>
                </>
              ) : (
                <Button appearance="primary" onClick={onClose}>
                  {strings.report.close}
                </Button>
              )
            ) : (
              <>
                <Button appearance="secondary" onClick={onClose} disabled={submitting}>
                  {strings.report.cancel}
                </Button>
                <Button
                  appearance="primary"
                  onClick={() => submit(false)}
                  disabled={submitting || description.trim().length < 3}
                >
                  {strings.report.submit}
                </Button>
              </>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
